import './bootstrap.js';
import '../css/app.css';
import '../css/layout/layout.css';

import type {DefineComponent} from 'vue';
import {createApp, h} from 'vue';
import {createInertiaApp, router} from '@inertiajs/vue3';
import NProgress from 'nprogress';
import {resolvePageComponent} from 'laravel-vite-plugin/inertia-helpers';
import {ZiggyVue} from 'ziggy-js';
import {createPinia} from 'pinia';

import CentralLayout from '@/views/layouts/central/layout-central.vue';
import TenantLayout from '@/views/layouts/tenant/layout-tenant.vue';
import RouterLink from '../views/components/router-link.vue';
import {i18nVue} from 'laravel-vue-i18n';
import posthog from 'posthog-js';
import * as Sentry from '@sentry/vue';
import {z} from 'zod';
import {makeZodI18nMap} from 'zod-vue-i18n';
import {createI18n} from 'vue-i18n';
import en from '@/scripts/datasets/zod/en.json';
import de from '@/scripts/datasets/zod/de.json';
import {useCookieConsentStore} from '@/scripts/stores/cookie-consent';
import CookieConsentBanner from '@/views/components/cookie-consent-banner.vue';
import CookieSettingsModal from '@/views/components/cookie-settings-modal.vue';

createInertiaApp({
	// disabled because of missing nonce support for CSP
	// styles add manually in _progress.css
	progress: false,
	resolve: (name) => {
		const page = resolvePageComponent<DefineComponent>(
			`../views/pages/${name}.vue`,
			import.meta.glob<DefineComponent>('../views/pages/**/*.vue')
		);

		let layout = CentralLayout;
		if (name.startsWith('tenant/')) {
			layout = TenantLayout;
		}

		page.then((module) => {
			if (module.default.layout === undefined) {
				module.default.layout = layout;
			}
		});
		return page;
	},
	setup({el, App, props, plugin}) {
		const pinia = createPinia();
		let isAppMounted = false;

		el.classList.add('h-full');

		const app = createApp({
			render: () => h(App, props),
			mounted() {
				// Initialize cookie consent after app is mounted
				const cookieStore = useCookieConsentStore();
				cookieStore.loadConsent();
			},
		})
			.use(plugin)
			.use(pinia)
			.use(ZiggyVue)
			.provide('posthog', posthog)
			.component('RouterLink', RouterLink)
			.component('CookieConsentBanner', CookieConsentBanner)
			.component('CookieSettingsModal', CookieSettingsModal)
			.use(i18nVue, {
				resolve: (lang: string) => import(`../../lang/${lang}.json`),
				fallBackLang: 'en',
				fallbackMissingTranslations: true,
				onLoad: (lang: string) => {
					const i18n = createI18n({
						locale: lang,
						fallbackLocale: 'en',
						messages: {
							en,
							de,
						},
					});
					z.setErrorMap(makeZodI18nMap(i18n));
					if (!isAppMounted) {
						app.mount(el);
						isAppMounted = true;
					}
				},
			});

		// Initialize analytics only after consent - moved to cookie store
		// PostHog and Sentry initialization is now handled by the cookie consent store

		Sentry.init({
			app,
			dsn: 'https://3fb8e5a41d9ab59d35398e730edcb998@o4507415007199232.ingest.de.sentry.io/4509243369193552',
			// Setting this option to true will send default PII data to Sentry.
			// For example, automatic IP address collection on events
			sendDefaultPii: true,
			integrations: [
				Sentry.browserTracingIntegration(),
				Sentry.replayIntegration(),
			],
			// Tracing
			tracesSampleRate: 1.0, // Capture 100% of the transactions
			// Session Replay
			replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
			replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
		});

		router.on('before', (event) => {
			if (event.detail.visit.only.length) {
				event.detail.visit.only.push('flashMessages');
			}
		});

		let timeout = null;

		router.on('start', () => {
			timeout = setTimeout(() => NProgress.start(), 250);
		});

		router.on('progress', (event) => {
			if (NProgress.isStarted() && event.detail.progress.percentage) {
				NProgress.set((event.detail.progress.percentage / 100) * 0.9);
			}
		});

		router.on('finish', (event) => {
			clearTimeout(timeout);
			if (!NProgress.isStarted()) {
				return;
			} else if (event.detail.visit.completed) {
				NProgress.done();
			} else if (event.detail.visit.interrupted) {
				NProgress.set(0);
			} else if (event.detail.visit.cancelled) {
				NProgress.done();
				NProgress.remove();
			}
		});
	},
});
